// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useBlocker, useNavigate } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGroupDetailQuery } from "@/api/hooks/use-groups";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { UnsavedChangesDialog } from "@/components/shared/unsaved-changes-dialog";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GroupBoPermissionsTab, type GroupBoPermissionsTabHandle } from "@/features/iam/group-bo-permissions-tab";
import { GroupInfoTab } from "@/features/iam/group-info-tab";
import { GroupPermissionsTab, type GroupPermissionsTabHandle } from "@/features/iam/group-permissions-tab";
import { GroupUsersTab } from "@/features/iam/group-users-tab";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

interface GroupDetailProps {
  groupName: string;
  tab?: string;
  initialFilters?: string;
}

export function GroupDetail({ groupName, tab, initialFilters }: GroupDetailProps) {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();

  const { data, isLoading, isError } = useGroupDetailQuery(groupName);
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PAGE_PERMISSIONS.iamGroupsEdit);

  // Fall back to the first allowed tab when an unavailable one is requested.
  const requestedTab = tab ?? "info";
  const tabAllowed: Record<string, boolean> = {
    info: true,
    users: true,
    permissions: canEdit,
    "bo-permissions": canEdit,
  };
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";

  // Unsaved-changes guard for the two editable permission tabs.
  const [isPermissionsDirty, setIsPermissionsDirty] = useState(false);
  const [isBoPermissionsDirty, setIsBoPermissionsDirty] = useState(false);
  const permissionsTabRef = useRef<GroupPermissionsTabHandle>(null);
  const boPermissionsTabRef = useRef<GroupBoPermissionsTabHandle>(null);
  const isFormDirty = isPermissionsDirty || isBoPermissionsDirty;
  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty,
    enableBeforeUnload: () => isFormDirty,
    withResolver: true,
  });

  const breadcrumbs = useMemo(() => [{ label: t("groups.title"), to: "/iam/groups" }], [t]);

  function handleTabChange(newTab: string) {
    navigate({
      to: "/iam/groups/$groupName",
      params: { groupName },
      search: newTab === "info" ? {} : { t: newTab },
      replace: true,
    });
  }

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div>
        <PageHeader title={groupName} breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  const { group } = data;

  return (
    <div>
      <PageHeader title={group.name} subtitle={<IdPill id={group.groupId} />} breadcrumbs={breadcrumbs} />
      <div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
            <TabsList>
              <TabsIndicator />
              <TabsTrigger value="info">{t("tabs.info")}</TabsTrigger>
              <TabsTrigger value="users">{t("tabs.users")}</TabsTrigger>
              {canEdit && <TabsTrigger value="permissions">{t("tabs.permissions")}</TabsTrigger>}
              {canEdit && <TabsTrigger value="bo-permissions">{t("tabs.boPermissions")}</TabsTrigger>}
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <GroupInfoTab groupName={groupName} />
          </TabsContent>
          <TabsContent value="users" className="space-y-4">
            <GroupUsersTab groupName={groupName} initialFilters={activeTab === "users" ? initialFilters : undefined} />
          </TabsContent>
          {canEdit && (
            <TabsContent value="permissions" className="space-y-4">
              <GroupPermissionsTab
                ref={permissionsTabRef}
                groupName={groupName}
                canEdit={canEdit}
                onDirtyChange={setIsPermissionsDirty}
              />
            </TabsContent>
          )}
          {canEdit && (
            <TabsContent value="bo-permissions" className="space-y-4">
              <GroupBoPermissionsTab
                ref={boPermissionsTabRef}
                groupName={groupName}
                canEdit={canEdit}
                onDirtyChange={setIsBoPermissionsDirty}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
      <UnsavedChangesDialog
        open={blocker.status === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => {
          permissionsTabRef.current?.reset();
          boPermissionsTabRef.current?.reset();
          blocker.proceed?.();
        }}
      />
    </div>
  );
}
